import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getOrder, downloadReceipt, updateOrder } from "../../utils/backend";
import { CTabs, CTabList, CTabContent, CTab, CTabPanel } from "@coreui/react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import { CCol, CFormLabel, CFormInput } from "@coreui/react-pro";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";

const ViewOrderAPI = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [manualPaymentLink, setManualPaymentLink] = useState("");
  const [extraAmount, setExtraAmount] = useState("");
  const [order, setOrder] = useState({});
  const { id } = useParams();

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await getOrder({ id: id });
      setOrder(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setOrder({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate("/orders");
  };

  const handleDownloadReceipt = async (orderId) => {
    try {
      const response = await downloadReceipt({ order_id: orderId });
      if (response.success) {
        if (response.data.receipt) {
          const link = document.createElement("a");
          link.href = response.data.receipt;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("Receipt downloaded successfully");
        } else {
          toast.error("Receipt not found");
        }
      } else {
        throw new Error(response.message || "Failed to download receipt");
      }
    } catch (error) {
      console.error("Error downloading receipt:", error);
      toast.error("Error downloading receipt");
    }
  };

  const handleAddManualPayment = async () => {
    try {
      const urlPattern =
        /^(https?:\/\/)?([\w\-])+\.{1}([a-zA-Z]{2,63})([\/\w\-\.\?=&]*)*\/?$/;

      if (manualPaymentLink === "" || !urlPattern.test(manualPaymentLink)) {
        toast.error("Manual Payment Link must be in a valid URL format");
        return;
      }

      const response = await updateOrder({
        id,
        manual_payment_link: manualPaymentLink,
        extra_amount: extraAmount,
      });

      if (response.success) {
        toast.success("Manual Link Updated successfully");
        fetchData(id);
      } else {
        throw new Error(response.message || "Failed to update manual link");
      }
    } catch (error) {
      console.error("Error adding manual payment link:", error);
      toast.error("Error adding manual payment link");
    }
  };

  const renderOrderDetails = (data) => {
    const excludedKeys = [
      "foreign_worker_insurance",
      "term_life_insurance",
      "health_insurance",
      "pa_insurance",
      "motor_insurance",
      "offshore_insurance",
      "group_insurance",
      "property_insurance",
    ];

    return Object.keys(data).map((key) => {
      const value = data[key];
      if (
        excludedKeys.includes(key) ||
        (typeof value === "object" && value !== null)
      ) {
        return null;
      }
      const formattedValue =
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}/.test(value) &&
        !isNaN(Date.parse(value))
          ? formatDateTime(value)
          : value;
      return (
        <div className="col-md-4 mb-3" key={key}>
          <label className="form-label">{key.replace(/_/g, " ")}</label>
          <p>
            <b>
              {formattedValue !== null && formattedValue !== ""
                ? formattedValue
                : "N/A"}
            </b>
          </p>
        </div>
      );
    });
  };

  const InsuranceDetails = ({ data }) => {
    const { insurance_type } = data?.order_details || {};

    const insuranceTypeMapping = {
      1: "Foreign Worker",
      2: "Term Life",
      3: "Health Insurance",
      4: "Personal Accident",
      5: "Motor",
      6: "Offshore Insurance",
      7: "Group Insurance",
      8: "Property Insurance",
    };

    const getInsuranceData = () => {
      switch (insurance_type) {
        case 1:
          return data.foreign_worker_insurance;
        case 2:
          return data.term_life_insurance;
        case 3:
          return data.health_insurance;
        case 4:
          return data.pa_insurance;
        case 5:
          return data.motor_insurance;
        case 6:
          return data.offshore_insurance;
        case 7:
          return data.group_insurance;
        case 8:
          return data.property_insurance;
        default:
          return {};
      }
    };

    const insuranceData = getInsuranceData();

    const renderValue = (value) => {
      if (Array.isArray(value) && value.length === 0) {
        return <strong>N/A</strong>;
      }

      if (typeof value === "object" && value !== null) {
        return (
          <ul>
            {Object.entries(value).map(([key, val]) => (
              <li key={key}>
                <strong>{key.replace(/_/g, " ")}:</strong> {renderValue(val)}
              </li>
            ))}
          </ul>
        );
      }

      if (
        value &&
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}/.test(value) &&
        !isNaN(Date.parse(value))
      ) {
        return formatDateTime(value);
      }

      if (typeof value === "string" && value.startsWith("uploads/")) {
        const baseURL = process.env.REACT_APP_API_URL + "/";
        return (
          <a
            href={`${baseURL}${value}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value.split("/").pop() || "N/A"}
          </a>
        );
      }

      return <strong>{value ? value.toString() : "N/A"}</strong>;
    };

    return (
      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label">Insurance Plan Type:</label>
          <p>
            <b>{insuranceTypeMapping[insurance_type] || "N/A"}</b>
          </p>
        </div>
        {insuranceData &&
          Object.keys(insuranceData).map((key) => (
            <div className="col-md-4 mb-3" key={key}>
              <label className="form-label">{key.replace(/_/g, " ")}:</label>
              <p>
                <b>{renderValue(insuranceData[key])}</b>
              </p>
            </div>
          ))}
      </div>
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <div className="card border-light">
          <div className="card-body p-4">
            <CTabs activeItemKey="order-details">
              <CTabList variant="underline">
                <CTab itemKey="order-details">Order Details</CTab>
                <CTab itemKey="insurance-details">Insurance Details</CTab>
              </CTabList>
              <CTabContent>
                <CTabPanel className="p-3" itemKey="order-details">
                  <div className="row">{renderOrderDetails(order)}</div>
                </CTabPanel>
                <CTabPanel className="p-3" itemKey="insurance-details">
                  <div className="row">
                    <InsuranceDetails data={order} />
                  </div>
                </CTabPanel>
              </CTabContent>
            </CTabs>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <CCol md="4" className="mb-3 me-3">
                  <CFormLabel>Manual Payment Link (URL)</CFormLabel>
                  <CFormInput
                    type="text"
                    onChange={(e) => setManualPaymentLink(e.target.value)}
                  />
                </CCol>

                <CCol md="4" className="mb-3 me-3">
                  <CFormLabel>Extra Amount (RM)</CFormLabel>
                  <CFormInput
                    type="number"
                    step="0.01"
                    min="0"
                    onChange={(e) => setExtraAmount(e.target.value)}
                  />
                </CCol>

                <div>
                  <button
                    type="button"
                    className="btn me-3 ansa-button-primary"
                    onClick={() => handleAddManualPayment()}
                  >
                    Add Manual Payment Link
                  </button>
                </div>
              </div>

              <div className="d-flex gap-2">
                {(order.order_status === "Paid" ||
                  order.order_status === "Completed") && (
                  <button
                    type="button"
                    className="btn me-3 ansa-button-primary"
                    onClick={() => handleDownloadReceipt(order.id)}
                  >
                    Download Receipt
                  </button>
                )}

                <button
                  type="button"
                  className="btn me-3 ansa-button-secondary"
                  onClick={handleBackClick}
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewOrderAPI;
